import { Anchor, Descriptions, Divider, Empty, Skeleton } from 'antd'
import { createStyles } from 'antd-style'
import { isArray, isString } from 'lodash'

import { isEmpty } from '@eastcoal/common/utils/object'

import { RichEditor } from '@eastcoal/editor/index'

import { useMallLayout } from '../../../../../../layouts/MallLayout/hooks'
import { TradeSessionDetailCard } from '../../DetailCard'
import { DetailImageGallery } from '../../ImageGallery'

import { ProductDescriptionCardProps } from './interface'

const useStyles = createStyles(({ prefixCls, token }) => ({
  card: {
    padding: 0
  },
  header: {
    margin: 0,
    padding: 0,
    [`& .${prefixCls}-anchor`]: {
      width: '100%',
      paddingInline: 0,
      backgroundColor: '#EFEFEF'
    },
    [`& .${prefixCls}-anchor-ink`]: {
      display: 'none'
    },
    [`& .${prefixCls}-anchor-link-title`]: {
      paddingInline: token.paddingXL,
      paddingBlock: token.paddingXS
    },
    [`& .${prefixCls}-anchor-link-active`]: {
      backgroundColor: token.colorWhite
    }
  },
  body: {
    padding: token.paddingMD
  },
  divider: {
    [`&.${prefixCls}-divider-horizontal`]: {
      color: token.yellow8,
      borderColor: token.yellow8,
      fontSize: token.fontSizeSM
    }
  }
}))

export const ProductDescriptionCard = ({
  loading,
  ...restProps
}: ProductDescriptionCardProps) => {
  const items: any[] = []

  const descriptionExist = 'description' in restProps
  const quotaExist = 'quota' in restProps
  const pictureExist = 'picture' in restProps

  let isAllEmpty = true

  if (descriptionExist) {
    const description = restProps.description
    const isNotEmpty = !!description

    if (isNotEmpty) isAllEmpty = false

    items.push({
      key: 'description',
      title: '声明信息',
      href: '#description',
      isNotEmpty,
      content: (
        <div id='description'>
          {description && <RichEditor readPretty value={description} />}
        </div>
      )
    })
  }

  if (quotaExist) {
    const quota = restProps.quota
    const isNotEmpty = !!quota

    if (isNotEmpty) isAllEmpty = false

    const quotaDom = isArray(quota) ? (
      <Descriptions
        bordered={false}
        column={4}
        size='small'
        items={quota
          .filter(item => item.visible !== false)
          .map(({ name, value1, value2, unit }) => ({
            label: name,
            children: isEmpty(value2)
              ? `${value1}${unit}`
              : `${value1}~${value2}${unit}`
          }))}
      />
    ) : isString(quota) && !!quota.trim() ? (
      <RichEditor readPretty value={quota} />
    ) : null

    items.push({
      key: 'quota',
      title: '指标信息',
      href: '#quota',
      isNotEmpty,
      content: <div id='quota'>{quotaDom}</div>
    })
  }

  if (pictureExist) {
    const picture = restProps.picture
    const isNotEmpty = !!picture

    if (isNotEmpty) isAllEmpty = false

    items.push({
      key: 'picture',
      title: '图片信息',
      href: '#picture',
      isNotEmpty,
      content: (
        <div id='picture'>
          <DetailImageGallery ossIds={picture} />
        </div>
      )
    })
  }

  const emptyMessage = isAllEmpty && <Empty description='没有相关信息~' />

  const { containerRef } = useMallLayout()
  const { styles } = useStyles()

  return (
    <TradeSessionDetailCard
      className={styles.card}
      classNames={{ body: styles.body }}
      title={
        <Anchor
          affix
          className={styles.header}
          direction='horizontal'
          getContainer={() => containerRef.current ?? window}
          items={items}
          showInkInFixed={false}
          targetOffset={96}
        />
      }
    >
      {items.map(({ isNotEmpty, title, content }) => (
        <Skeleton
          key={title}
          loading={loading}
          paragraph={{
            rows: 5
          }}
        >
          {isNotEmpty && (
            <Divider className={styles.divider} orientation='horizontal'>
              {title}
            </Divider>
          )}
          {content}
        </Skeleton>
      ))}
      {emptyMessage}
    </TradeSessionDetailCard>
  )
}
